import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Wand2, ChevronDown, ChevronUp, Copy, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { copyFormattedContent } from "@/lib/utils";

// Helper function to get first few sentences (max 3) from content
const getContentPreview = (content: string, maxSentences = 3) => {
  if (!content) return "";
  
  // Match sentences ending with ., !, or ? followed by a space or end of string
  const sentenceRegex = /[^.!?]*[.!?](?:\s|$)/g;
  const sentences = content.match(sentenceRegex) || [];
  
  // Get the first few sentences (up to maxSentences)
  const preview = sentences.slice(0, maxSentences).join("");
  
  // If the preview is significantly shorter than the full content, add ellipsis
  return preview.length < content.length * 0.8 ? preview.trim() + "..." : preview.trim();
};

// Helper function to strip HTML tags from content
const stripHtmlTags = (html: string) => {
  if (!html) return "";
  return html.replace(/<\/?[^>]+(>|$)/g, "");
};

// Helper function to format markdown-style text (convert ** to bold)
const formatMarkdownText = (text: string) => {
  if (!text) return "";
  
  // Replace **text** with <strong>text</strong>
  return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
};

export default function CompetitorContent() {
  const { id: projectId } = useParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [content, setContent] = useState("");
  const [feedback, setFeedback] = useState("");
  const [selectedContentId, setSelectedContentId] = useState<string | null>(null);
  const [expandedContents, setExpandedContents] = useState<string[]>([]);
  const [feedbackOpenForId, setFeedbackOpenForId] = useState<string | null>(null);
  const [copiedContentId, setCopiedContentId] = useState<string | null>(null);

  // Query for competitor content results
  const { data: results, isLoading: isLoadingResults } = useQuery({
    queryKey: ["competitor-content", projectId],
    queryFn: async () => {
      const { data } = await supabase
        .from("competitor_content")
        .select("*")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false });
      return data;
    },
    staleTime: 0, // Consider data immediately stale
    gcTime: 30000, // Cache for 30 seconds only
    refetchOnWindowFocus: true,
  });

  // Mutation for analyzing content
  const analyzeMutation = useMutation({
    mutationFn: async () => {
      try {
        const { data, error } = await supabase.functions.invoke('analyze-competitor-content', {
          body: {
            content,
            metadata: {
              userId: user?.id,
            },
            projectId,
          },
        });
        
        if (error) {
          // Check if it's a rate limit error
          if (error.message?.includes('429') || error.message?.toLowerCase().includes('too many requests')) {
            throw new Error('Rate limit exceeded. Please wait a minute and try again.');
          }
          throw new Error(`Failed to analyze content: ${error.message}`);
        }
        return data;
      } catch (error) {
        throw new Error(`Failed to analyze content: ${error.message}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["competitor-content", projectId] });
      toast({
        title: "Success",
        description: "Content analyzed successfully!",
        variant: "success",
      });
      setContent("");
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Mutation for giving feedback and regenerating content
  const feedbackMutation = useMutation({
    mutationFn: async ({ contentId, feedback }: { contentId: string, feedback: string }) => {
      // First, get the original content
      const { data: contentData, error: fetchError } = await supabase
        .from("competitor_content")
        .select("enhanced_content, metadata")
        .eq("id", contentId)
        .single();

      if (fetchError) {
        throw new Error(`Failed to retrieve content: ${fetchError.message}`);
      }

      if (!contentData) {
        throw new Error("Content not found");
      }

      try {
        const { data, error } = await supabase.functions.invoke('review-competitor-content', {
          body: {
            content: contentData.enhanced_content,
            feedback,
            metadata: {
              ...(contentData.metadata as Record<string, unknown> || {}),
              userId: user?.id,
              contentId,
              isFeedback: true,
            },
          },
        });
        
        if (error) {
          // Check if it's a rate limit error
          if (error.message?.includes('429') || error.message?.toLowerCase().includes('too many requests')) {
            throw new Error('Rate limit exceeded. Please wait a minute and try again.');
          }
          throw new Error(`Failed to regenerate content: ${error.message}`);
        }
        
        return { data, contentId };
      } catch (error) {
        throw new Error(`Failed to regenerate content: ${error instanceof Error ? error.message : String(error)}`);
      }
    },
    onSuccess: (result) => {
      // Update the content directly in the results array
      if (results && result?.contentId) {
        const newContent = result.data?.analyzedContent || result.data?.improvedContent;
        
        if (!newContent) {
          console.error("No analyzedContent or improvedContent found in response:", result.data);
          toast({
            title: "Error",
            description: "Failed to regenerate content: Empty response received",
            variant: "destructive",
          });
          return;
        }
        
        // Determine if we need to structure the content as JSON or keep it as is
        let formattedContent = newContent;
        
        try {
          // Check if the original content was in JSON format
          const originalItem = results.find(item => item.id === result.contentId);
          if (originalItem && originalItem.enhanced_content) {
            try {
              // If original content was JSON, wrap the new content in the same format
              const originalParsed = JSON.parse(originalItem.enhanced_content);
              // Original was JSON, so wrap new content in similar structure
              formattedContent = JSON.stringify({
                enhanced_content: newContent,
                // Preserve any content analysis that might have existed
                content_analysis: originalParsed.content_analysis || {},
                improvements_made: [] // Reset improvements since this is regenerated content
              });
            } catch (e) {
              // If it was XML format
              if (originalItem.enhanced_content.includes('<competitor_content_analysis>')) {
                // Create XML structure
                const typeMatch = originalItem.enhanced_content.match(/<type>([\s\S]*?)<\/type>/);
                const audienceMatch = originalItem.enhanced_content.match(/<target_audience>([\s\S]*?)<\/target_audience>/);
                
                const contentType = typeMatch && typeMatch[1] ? typeMatch[1].trim() : "Unknown Type";
                const targetAudience = audienceMatch && audienceMatch[1] ? audienceMatch[1].trim() : "Unknown Audience";
                
                formattedContent = `<competitor_content_analysis>
  <type>${contentType}</type>
  <target_audience>${targetAudience}</target_audience>
  <enhanced_content>${newContent}</enhanced_content>
  <improvements_made></improvements_made>
</competitor_content_analysis>`;
              }
              // Otherwise keep it as is (plain text)
            }
          }
        } catch (error) {
          console.error("Error formatting regenerated content:", error);
          // If anything fails, just use the raw new content
          formattedContent = newContent;
        }
        
        const updatedResults = results.map(item => 
          item.id === result.contentId 
            ? { ...item, enhanced_content: formattedContent } 
            : item
        );
        
        // Set the updated results in the query cache
        queryClient.setQueryData(["competitor-content", projectId], updatedResults);
      }
      
      // Clear feedback and close form
      setFeedback("");
      setFeedbackOpenForId(null);
      
      // Update toast
      toast({
        title: "Success",
        description: "Content regenerated successfully!",
        variant: "success",
      });
      
      // Ensure the item stays expanded after regeneration
      if (selectedContentId && !expandedContents.includes(selectedContentId)) {
        setExpandedContents(prev => [...prev, selectedContentId]);
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleAnalyze = () => {
    if (!content.trim()) {
      toast({
        title: "Error",
        description: "Please enter some content to analyze",
        variant: "destructive",
      });
      return;
    }
    analyzeMutation.mutate();
  };

  const handleOpenFeedbackDialog = (contentId: string) => {
    if (!contentId) return;
    
    if (feedbackOpenForId === contentId) {
      // If already open for this content, close it
      setFeedbackOpenForId(null);
      setSelectedContentId(null);
    } else {
      // If previously open for a different content, close that first
      setFeedback("");
      setSelectedContentId(contentId);
      setFeedbackOpenForId(contentId);
      
      // Also make sure the content is expanded
      if (!expandedContents.includes(contentId)) {
        setExpandedContents(prev => [...prev, contentId]);
      }
    }
  };

  const handleSubmitFeedback = () => {
    if (!feedback.trim()) {
      toast({
        title: "Error",
        description: "Please enter feedback",
        variant: "destructive",
      });
      return;
    }
    
    if (!selectedContentId) {
      toast({
        title: "Error",
        description: "No content selected",
        variant: "destructive",
      });
      return;
    }

    // Add loading state
    toast({
      title: "Loading",
      description: "Regenerating content...",
    });
    
    // Trigger the feedback mutation
    feedbackMutation.mutate({ contentId: selectedContentId, feedback });
  };

  const toggleExpanded = (contentId: string) => {
    setExpandedContents(prev => 
      prev.includes(contentId) 
        ? prev.filter(id => id !== contentId) 
        : [...prev, contentId]
    );
  };

  const isExpanded = (contentId: string) => expandedContents.includes(contentId);

  const handleCopyContent = (content: string, contentId: string) => {
    copyFormattedContent(content, {
      onSuccess: () => {
        setCopiedContentId(contentId);
        // Reset the copied state after 2 seconds
        setTimeout(() => {
          setCopiedContentId(null);
        }, 2000);
      },
      onError: () => {
        toast({
          title: "Error",
          description: "Failed to copy text to clipboard",
          variant: "destructive",
        });
      }
    });
  };

  return (
    <div className="container py-6 max-w-7xl mx-auto">
      <div className="flex flex-col space-y-8">
        {/* Header with gradient background */}
        <div className="relative rounded-[32px] bg-gradient-to-br from-[#F8F9FF] via-[#F3F5FF] to-white dark:from-zinc-900 dark:via-zinc-900 dark:to-zinc-800 p-8 shadow-sm overflow-hidden">
          {/* Subtle background glow effect */}
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-blue-200 dark:bg-blue-900 rounded-full opacity-20 blur-3xl"></div>
          
          <div className="relative flex flex-col gap-4">
            <h1 className="text-3xl font-bold tracking-tight gradient-text">
              Repurpose Competitor Content
            </h1>
            <p className="text-zinc-500 dark:text-zinc-400 max-w-2xl">
              Analyze and enhance competitor content while maintaining its core message. Our AI will help you create more engaging and valuable content.
            </p>
          </div>
        </div>

        {/* Input Section with modern design */}
        <Card className="overflow-hidden border border-zinc-200 dark:border-zinc-700 shadow-sm dark:bg-zinc-900">
          <CardContent className="p-8">
            <div className="flex flex-col space-y-6">
              <div className="space-y-2">
                <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Analyze Content</h2>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">Paste your competitor's content below to analyze and enhance it</p>
              </div>
              <Textarea
                placeholder="Paste the content you want to analyze and enhance..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="min-h-[200px] resize-none bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 focus-visible:ring-blue-500 dark:text-zinc-100"
              />
              <div className="flex justify-end">
                <Button
                  onClick={handleAnalyze}
                  disabled={analyzeMutation.isPending}
                  className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-6 h-11 font-medium transition-all duration-200 hover:shadow-md active:scale-[0.98]"
                >
                  {analyzeMutation.isPending ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <Wand2 className="h-4 w-4" />
                  )}
                  Analyze & Enhance
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Section */}
        {isLoadingResults ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : results?.length === 0 ? (
          <Alert className="bg-gradient-to-r from-zinc-50 to-zinc-100 dark:from-zinc-800 dark:to-zinc-900 border border-zinc-200 dark:border-zinc-700">
            <AlertTitle className="text-zinc-900 dark:text-zinc-100 font-semibold">No results yet</AlertTitle>
            <AlertDescription className="text-zinc-600 dark:text-zinc-400">
              Paste some content above to get started with the analysis
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-8">
            {results?.map((result) => (
              <Collapsible 
                key={result.id} 
                open={isExpanded(result.id)}
                onOpenChange={() => toggleExpanded(result.id)}
                className="overflow-hidden transition-all duration-200"
              >
                <Card 
                  className="overflow-hidden border border-zinc-200 dark:border-zinc-700 shadow-sm dark:bg-zinc-900 hover:shadow-md transition-all duration-200"
                >
                  <CardContent 
                    className={`p-6 ${isExpanded(result.id) ? 'pb-3' : 'pb-6'}`}
                    onClick={(e) => {
                      // Don't trigger if user is selecting text
                      if (window.getSelection()?.toString()) {
                        return;
                      }
                      
                      // Don't trigger if the click was on an interactive element or its children
                      if (
                        e.target instanceof Element && 
                        (e.target.closest('button') || e.target.closest('a') || e.target.closest('.interactive'))
                      ) {
                        return;
                      }
                      
                      // Only open the card, never close it
                      if (!isExpanded(result.id)) {
                        toggleExpanded(result.id);
                      }
                    }}
                  >
                    <div className="flex flex-col space-y-4" data-result-id={result.id}>
                      {/* Content Type and Audience Badges */}
                      <div className="flex items-center gap-3">
                        {(() => {
                          try {
                            try {
                              // Try JSON format first
                              const enhancedContent = JSON.parse(result.enhanced_content);
                              return (
                                <>
                                  <Badge
                                    variant="outline"
                                    className="px-3 py-1 bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800"
                                  >
                                    {enhancedContent.content_analysis.type}
                                  </Badge>
                                  <Badge
                                    variant="outline"
                                    className="px-3 py-1 bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800"
                                  >
                                    {enhancedContent.content_analysis.target_audience}
                                  </Badge>
                                  {/* Show feedback badge if content was improved with feedback */}
                                  {result.metadata && 
                                   typeof result.metadata === 'object' && 
                                   'lastUpdateWasFeedback' in result.metadata && 
                                   result.metadata.lastUpdateWasFeedback && (
                                    <Badge
                                      variant="outline"
                                      className="px-3 py-1 bg-green-50 text-green-700 border-green-200 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800"
                                    >
                                      Feedback Improved
                                    </Badge>
                                  )}
                                </>
                              );
                            } catch (jsonError) {
                              // Try XML format
                              if (result.enhanced_content.includes('<competitor_content_analysis>')) {
                                const typeMatch = result.enhanced_content.match(/<type>([\s\S]*?)<\/type>/);
                                const audienceMatch = result.enhanced_content.match(/<target_audience>([\s\S]*?)<\/target_audience>/);
                                
                                const contentType = typeMatch && typeMatch[1] ? typeMatch[1].trim() : "Unknown Type";
                                const targetAudience = audienceMatch && audienceMatch[1] ? audienceMatch[1].trim() : "Unknown Audience";
                                
                                return (
                                  <>
                                    <Badge
                                      variant="outline"
                                      className="px-3 py-1 bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800"
                                    >
                                      {contentType}
                                    </Badge>
                                    <Badge
                                      variant="outline"
                                      className="px-3 py-1 bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800"
                                    >
                                      {targetAudience}
                                    </Badge>
                                    {/* Show feedback badge if content was improved with feedback */}
                                    {result.metadata && 
                                     typeof result.metadata === 'object' && 
                                     'lastUpdateWasFeedback' in result.metadata && 
                                     result.metadata.lastUpdateWasFeedback && (
                                      <Badge
                                        variant="outline"
                                        className="px-3 py-1 bg-green-50 text-green-700 border-green-200 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800"
                                      >
                                        Feedback Improved
                                      </Badge>
                                    )}
                                  </>
                                );
                              }
                              
                              throw new Error("Could not parse content in any format");
                            }
                          } catch (error) {
                            console.error("Error parsing content:", error, result.enhanced_content);
                            return (
                              <Badge variant="outline" className="px-3 py-1 bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800">
                                Error parsing content
                              </Badge>
                            );
                          }
                        })()}
                        
                        <CollapsibleTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="p-1 h-7 w-7 rounded-full ml-auto"
                          >
                            {isExpanded(result.id) ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                        </CollapsibleTrigger>
                      </div>

                      {/* Content Preview - Only show when NOT expanded */}
                      {!isExpanded(result.id) && (
                        <div className="font-sans text-sm text-zinc-700 dark:text-zinc-300 bg-zinc-50 dark:bg-zinc-800 rounded-lg p-4">
                          {(() => {
                            try {
                              if (!result.enhanced_content || result.enhanced_content.trim() === '') {
                                return "No enhanced content available yet. Processing may still be in progress.";
                              }
                              
                              let content = "";
                              // Try to parse as JSON first
                              try {
                                const parsed = JSON.parse(result.enhanced_content);
                                if (parsed.enhanced_content) {
                                  content = parsed.enhanced_content;
                                } else if (typeof parsed === 'object') {
                                  // If the structure is different, just stringify it
                                  content = JSON.stringify(parsed);
                                } else {
                                  content = "Error: Missing enhanced content in response";
                                }
                              } catch (jsonError) {
                                // If not valid JSON, try to extract content from XML
                                if (result.enhanced_content.includes('<competitor_content_analysis>')) {
                                  // Simple XML extraction 
                                  const match = result.enhanced_content.match(/<enhanced_content>([\s\S]*?)<\/enhanced_content>/);
                                  if (match && match[1]) {
                                    content = match[1].trim();
                                  }
                                } else {
                                  // If all else fails, return the raw content
                                  content = result.enhanced_content;
                                }
                              }
                              
                              return getContentPreview(content);
                            } catch (error) {
                              console.error("Error displaying enhanced content:", error);
                              return "Error processing content. Please try again.";
                            }
                          })()}
                        </div>
                      )}
                    </div>
                  </CardContent>

                  <CollapsibleContent>
                    {/* Expanded Content with Tabs */}
                    <CardContent className="pt-0 pb-6 px-6">
                      <Tabs defaultValue="enhanced" className="w-full">
                        <TabsList className="bg-zinc-50 dark:bg-zinc-800 p-1">
                          <TabsTrigger value="original" className="data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 data-[state=active]:shadow-sm">
                            Original Content
                          </TabsTrigger>
                          <TabsTrigger value="enhanced" className="data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 data-[state=active]:shadow-sm">
                            Enhanced Content
                          </TabsTrigger>
                          <TabsTrigger value="improvements" className="data-[state=active]:bg-white dark:data-[state=active]:bg-zinc-700 data-[state=active]:shadow-sm">
                            Improvements Made
                          </TabsTrigger>
                        </TabsList>

                        <TabsContent value="original" className="mt-6">
                          <div className="prose max-w-none">
                            <div className="relative whitespace-pre-wrap font-sans text-sm text-zinc-700 dark:text-zinc-300 bg-zinc-50 dark:bg-zinc-800 rounded-lg p-4 max-h-96 overflow-y-auto">
                              <div dangerouslySetInnerHTML={{ __html: formatMarkdownText(result.original_content) }} />
                              <div className="absolute top-2 right-2 bg-white dark:bg-zinc-700 rounded-md shadow-sm">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleCopyContent(result.original_content, `original-${result.id}`)}
                                  className="flex items-center gap-1 px-2 border border-zinc-200 dark:border-zinc-600"
                                  title="Copy to clipboard"
                                >
                                  {copiedContentId === `original-${result.id}` ? (
                                    <>
                                      <Check className="h-4 w-4 text-green-500" />
                                      <span className="text-xs">Copied</span>
                                    </>
                                  ) : (
                                    <>
                                      <Copy className="h-4 w-4" />
                                      <span className="text-xs">Copy</span>
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </TabsContent>

                        <TabsContent value="enhanced" className="mt-6">
                          <div className="prose max-w-none">
                            <div className="flex justify-end mb-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  let contentToCopy = "";
                                  try {
                                    // Try to parse as JSON first
                                    try {
                                      const parsed = JSON.parse(result.enhanced_content);
                                      if (parsed.enhanced_content) {
                                        contentToCopy = parsed.enhanced_content;
                                      } else {
                                        contentToCopy = result.enhanced_content;
                                      }
                                    } catch (jsonError) {
                                      // If not valid JSON, try to extract content from XML
                                      if (result.enhanced_content.includes('<competitor_content_analysis>')) {
                                        const match = result.enhanced_content.match(/<enhanced_content>([\s\S]*?)<\/enhanced_content>/);
                                        if (match && match[1]) {
                                          contentToCopy = match[1].trim();
                                        } else {
                                          contentToCopy = result.enhanced_content;
                                        }
                                      } else {
                                        contentToCopy = result.enhanced_content;
                                      }
                                    }
                                    handleCopyContent(contentToCopy, `enhanced-${result.id}`);
                                  } catch (error) {
                                    console.error("Error copying enhanced content:", error);
                                    toast({
                                      title: "Error",
                                      description: "Failed to copy content",
                                      variant: "destructive",
                                    });
                                  }
                                }}
                                className="flex items-center gap-1 px-2 border border-zinc-200 dark:border-zinc-600"
                                title="Copy to clipboard"
                              >
                                {copiedContentId === `enhanced-${result.id}` ? (
                                  <>
                                    <Check className="h-4 w-4 text-green-500" />
                                    <span className="text-xs">Copied</span>
                                  </>
                                ) : (
                                  <>
                                    <Copy className="h-4 w-4" />
                                    <span className="text-xs">Copy</span>
                                  </>
                                )}
                              </Button>
                            </div>
                            <div className="relative whitespace-pre-wrap font-sans text-sm text-zinc-700 dark:text-zinc-300 bg-zinc-50 dark:bg-zinc-800 rounded-lg p-4 max-h-96 overflow-y-auto">
                              <div 
                                dangerouslySetInnerHTML={{ 
                                  __html: (() => {
                                    try {
                                      if (!result.enhanced_content || result.enhanced_content.trim() === '') {
                                        return "No enhanced content available yet. Processing may still be in progress.";
                                      }
                                      
                                      let enhancedContent = "";
                                      // Try to parse as JSON first
                                      try {
                                        const parsed = JSON.parse(result.enhanced_content);
                                        // Ensure we're accessing the correct property
                                        if (parsed.enhanced_content) {
                                          enhancedContent = parsed.enhanced_content;
                                        } else if (typeof parsed === 'object') {
                                          // If the structure is different, check if it's directly the content
                                          enhancedContent = JSON.stringify(parsed, null, 2);
                                        } else {
                                          enhancedContent = "Error: Missing enhanced content in response";
                                        }
                                      } catch (jsonError) {
                                        // If not valid JSON, try to extract content from XML
                                        if (result.enhanced_content.includes('<competitor_content_analysis>')) {
                                          // Simple XML extraction 
                                          const match = result.enhanced_content.match(/<enhanced_content>([\s\S]*?)<\/enhanced_content>/);
                                          if (match && match[1]) {
                                            enhancedContent = match[1].trim();
                                          }
                                        } else {
                                          // If all else fails, return the raw content
                                          enhancedContent = result.enhanced_content;
                                        }
                                      }
                                      
                                      // Apply markdown formatting to the content
                                      return formatMarkdownText(enhancedContent);
                                    } catch (error) {
                                      console.error("Error displaying enhanced content:", error);
                                      return "Error processing content. Please try again.";
                                    }
                                  })()
                                }} 
                              />
                            </div>
                          </div>
                        </TabsContent>

                        <TabsContent value="improvements" className="mt-6">
                          <div className="space-y-4">
                            {(() => {
                              try {
                                // First try parsing as JSON
                                try {
                                  const parsed = JSON.parse(result.enhanced_content);
                                  if (parsed.improvements_made && Array.isArray(parsed.improvements_made)) {
                                    if (parsed.improvements_made.length === 0) {
                                      return (
                                        <div className="bg-yellow-50 dark:bg-yellow-900/30 rounded-lg p-6 border border-yellow-200 dark:border-yellow-800">
                                          <p className="text-sm text-yellow-700 dark:text-yellow-400">
                                            No improvements listed. This could be because the content was regenerated based on feedback.
                                          </p>
                                        </div>
                                      );
                                    }
                                    
                                    return parsed.improvements_made.map((improvement, index) => (
                                      <div
                                        key={index}
                                        className="bg-zinc-50 dark:bg-zinc-800 rounded-lg p-6 border border-zinc-200 dark:border-zinc-700 space-y-2 transition-all duration-200 hover:shadow-sm"
                                      >
                                        <h3 className="font-medium text-zinc-900 dark:text-zinc-100">
                                          {improvement.aspect}
                                        </h3>
                                        <p className="text-sm text-zinc-600 dark:text-zinc-400">
                                          {improvement.explanation}
                                        </p>
                                      </div>
                                    ));
                                  }
                                } catch (jsonError) {
                                  // If not valid JSON, try to extract from XML
                                  if (result.enhanced_content.includes('<competitor_content_analysis>')) {
                                    // Simple regex to find improvements
                                    const regex = /<improvement>\s*<aspect>([\s\S]*?)<\/aspect>\s*<explanation>([\s\S]*?)<\/explanation>\s*<\/improvement>/g;
                                    const improvements = [];
                                    let match;
                                    
                                    while ((match = regex.exec(result.enhanced_content)) !== null) {
                                      improvements.push({
                                        aspect: match[1].trim(),
                                        explanation: match[2].trim()
                                      });
                                    }
                                    
                                    if (improvements.length > 0) {
                                      return improvements.map((improvement, index) => (
                                        <div
                                          key={index}
                                          className="bg-zinc-50 dark:bg-zinc-800 rounded-lg p-6 border border-zinc-200 dark:border-zinc-700 space-y-2 transition-all duration-200 hover:shadow-sm"
                                        >
                                          <h3 className="font-medium text-zinc-900 dark:text-zinc-100">
                                            {improvement.aspect}
                                          </h3>
                                          <p className="text-sm text-zinc-600 dark:text-zinc-400">
                                            {improvement.explanation}
                                          </p>
                                        </div>
                                      ));
                                    }
                                  }
                                }
                                
                                return (
                                  <div className="bg-yellow-50 dark:bg-yellow-900/30 rounded-lg p-6 border border-yellow-200 dark:border-yellow-800">
                                    <p className="text-sm text-yellow-700 dark:text-yellow-400">
                                      No improvements found in the response. The content might still be processing.
                                    </p>
                                  </div>
                                );
                              } catch (error) {
                                return (
                                  <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-6 border border-red-200 dark:border-red-800">
                                    <p className="text-sm text-red-700 dark:text-red-400">Error parsing improvements</p>
                                  </div>
                                );
                              }
                            })()}
                          </div>
                        </TabsContent>
                      </Tabs>

                      {/* Action Buttons with modern styling */}
                      <div className="flex flex-col gap-3 pt-6">
                        {/* Feedback form that appears when feedbackOpenForId matches the current result id */}
                        {feedbackOpenForId === result.id && (
                          <div className="mb-4 border border-zinc-200 dark:border-zinc-700 rounded-lg p-4 bg-zinc-50 dark:bg-zinc-800 shadow-sm transition-all duration-200">
                            <div className="space-y-4">
                              <div className="space-y-2">
                                <h4 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Provide feedback for regeneration</h4>
                                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                                  Provide specific feedback on what you'd like to improve in the content
                                </p>
                              </div>
                              <Textarea
                                placeholder="e.g., Make it more conversational, add more examples, focus on benefits..."
                                value={feedback}
                                onChange={(e) => setFeedback(e.target.value)}
                                className="min-h-[120px] resize-none bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700"
                                disabled={feedbackMutation.isPending}
                              />
                              <div className="flex justify-end gap-2">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => {
                                    setFeedbackOpenForId(null);
                                    setFeedback("");
                                  }}
                                  disabled={feedbackMutation.isPending}
                                  className="text-sm"
                                >
                                  Cancel
                                </Button>
                                <Button 
                                  size="sm"
                                  onClick={handleSubmitFeedback}
                                  disabled={feedbackMutation.isPending || !feedback.trim()}
                                  className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm"
                                >
                                  {feedbackMutation.isPending ? <LoadingSpinner size="sm" /> : "Submit & Regenerate"}
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Button to toggle feedback form */}
                        <div className="flex justify-end">
                          <Button
                            variant={feedbackOpenForId === result.id ? "default" : "outline"}
                            onClick={() => handleOpenFeedbackDialog(result.id)}
                            disabled={feedbackMutation.isPending}
                            className={`gap-2 transition-all duration-200 hover:shadow-sm active:scale-[0.98] ${
                              feedbackOpenForId === result.id 
                                ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                                : "border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                            }`}
                          >
                            {feedbackMutation.isPending && selectedContentId === result.id ? (
                              <>
                                <LoadingSpinner size="sm" />
                                <span>Regenerating...</span>
                              </>
                            ) : (
                              feedbackOpenForId === result.id ? "Cancel Feedback" : "Give Feedback & Regenerate"
                            )}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))}
          </div>
        )}
      </div>
    </div>
  );
} 